import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FieldArray, type FormikProps } from 'formik';
import { forwardRef, useImperativeHandle, useMemo } from 'react';

import './SpecificationDimensionsPopupContent.scss';

import { SelectField } from 'src/components/base/fields/SelectField';
import type { SpecificationDimensionsPopupFormValues } from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsPopup.validation';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import type { ISelectOption } from 'src/types';
import {
  SPECIFICATION_MEASURE_UNIT_MAPPER,
  SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER,
  SpecificationMeasureUnit,
} from 'src/types/reference/SpecificationMeasureUnit';

function MeasureUnitOptionRender({ value }: { value?: string | number }) {
  return `${SPECIFICATION_MEASURE_UNIT_MAPPER[value as SpecificationMeasureUnit]} (${SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[value as SpecificationMeasureUnit]})`;
}

export type SpecificationDimensionsPopupFormContentRef = {
  submit?: () => Promise<void>;
  isDisabled?: boolean;
};

type SpecificationDimensionsPopupFormContentProps = {
  formikProps: FormikProps<SpecificationDimensionsPopupFormValues>;
  is3d: boolean;

  multiselect: boolean;
  defaultMeasureUnit: SpecificationMeasureUnit | null;
  allowedMeasureUnits: SpecificationMeasureUnit[];
};

export const SpecificationDimensionsPopupFormContent = forwardRef<
  SpecificationDimensionsPopupFormContentRef,
  SpecificationDimensionsPopupFormContentProps
>(
  (
    { formikProps: { values, isValid, submitForm }, is3d, multiselect, defaultMeasureUnit, allowedMeasureUnits },
    ref
  ) => {
    useImperativeHandle<SpecificationDimensionsPopupFormContentRef, SpecificationDimensionsPopupFormContentRef>(
      ref,
      () => ({
        submit: submitForm,
        isDisabled: !isValid,
      }),
      [submitForm, isValid]
    );

    const measureUnitsOptions: ISelectOption[] = useMemo(
      () =>
        allowedMeasureUnits.map((allowedMeasureUnit) => ({
          label: SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[allowedMeasureUnit],
          value: allowedMeasureUnit,
        })),
      [allowedMeasureUnits]
    );

    return (
      <div className="specification-dimensions-popup-content__container">
        <FieldArray
          name="values"
          render={(arrayHelpers) => {
            const hasMeasureUnits = multiselect && measureUnitsOptions.length > 0;

            const gridLayoutItems = [
              '1fr', // Length
              '1fr', // Width,
              ...(is3d ? ['1fr'] : []), // Height

              ...(hasMeasureUnits ? ['70px'] : []), // Measure units
              ...(multiselect ? ['max-content'] : []), // Remove item button
            ].join(' ');

            return (
              <>
                {values.values.map((_, index) => (
                  <div
                    key={String(index)}
                    className="specification-dimensions-popup-content__item"
                    style={{
                      gridTemplateColumns: gridLayoutItems,
                    }}
                  >
                    <TextInputField
                      name={`values[${index}].length`}
                      type="number"
                      label="Length"
                      touchOnMount={true}
                      autoFocus={true}
                      addonAfter={
                        !multiselect &&
                        defaultMeasureUnit &&
                        defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                          <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                        )
                      }
                    />
                    <TextInputField
                      name={`values[${index}].width`}
                      type="number"
                      label="Width"
                      touchOnMount={true}
                      addonAfter={
                        !multiselect &&
                        defaultMeasureUnit &&
                        defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                          <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                        )
                      }
                    />
                    {is3d && (
                      <TextInputField
                        name={`values[${index}].height`}
                        type="number"
                        label="Height"
                        touchOnMount={true}
                        addonAfter={
                          !multiselect &&
                          defaultMeasureUnit &&
                          defaultMeasureUnit !== SpecificationMeasureUnit.NONE && (
                            <span>{SPECIFICATION_MEASURE_UNIT_SHORT_MAPPER[defaultMeasureUnit]}</span>
                          )
                        }
                      />
                    )}
                    {hasMeasureUnits && (
                      <SelectField
                        className="specification-dimensions-popup-content__measure-units"
                        name={`values[${index}].measureUnit`}
                        options={measureUnitsOptions}
                        popupMatchSelectWidth={false}
                        touchOnMount={true}
                        optionRender={({ value }) => <MeasureUnitOptionRender value={value} />}
                        getPopupContainer={() =>
                          // Render popup in current element to get click events on dropdown inside `specification-dimensions-popup` element
                          (document.getElementsByClassName('specification-dimensions-popup')?.[0] as HTMLElement) ??
                          document.body
                        }
                      />
                    )}
                    {multiselect && (
                      <Button
                        className="specification-dimensions-popup-content__button"
                        title="Remove value"
                        type="primary"
                        danger={true}
                        size="middle"
                        icon={<MinusOutlined />}
                        disabled={values.values.length < 2}
                        onClick={arrayHelpers.handleRemove(index)}
                      />
                    )}
                  </div>
                ))}
                {multiselect && (
                  <Button
                    className="specification-dimensions-popup-content__button specification-dimensions-popup-content__add-button"
                    title="Add value"
                    type="primary"
                    size="middle"
                    icon={<PlusOutlined />}
                    onClick={arrayHelpers.handlePush({
                      measureUnit: defaultMeasureUnit,
                      length: null,
                      width: null,
                      height: null,
                    } as SpecificationDimensionsPopupFormValues['values'][0])}
                  />
                )}
              </>
            );
          }}
        />
      </div>
    );
  }
);
